import { createContext } from "react";
import type { AuthState, OAuthProvider } from "@/lib/supabase/types";
import type { ViewAsRecord } from "@/lib/supabase/viewAs";

/**
 * The Super Admin "view as user" session this tab is running inside.
 *
 * Present only in a tab opened through /admin/view-as/:userId. Everywhere else
 * this is null, and every consumer should treat null as the normal case.
 */
export interface ViewAsContext {
  record: ViewAsRecord;
  /** Ends the session and returns the tab to the Super Admin's own account. */
  exit: () => Promise<void>;
  /** Unblocks writes for the rest of the session. Recorded in the ledger. */
  enableActions: () => Promise<void>;
}

/**
 * Microsoft (Azure AD) is the only sign-in method. The password, magic-link and
 * self-registration entry points that used to hang off this context were
 * removed with the legacy login pages.
 */
export interface AuthContextValue extends AuthState {
  signInWithOAuth: (provider: OAuthProvider) => Promise<void>;
  /**
   * Inside a view-as session this ends the impersonation rather than signing
   * anybody out — see AuthProvider.exitViewAs.
   */
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  refreshSession: () => Promise<void>;
  viewAs: ViewAsContext | null;
}

export const AuthContext = createContext<AuthContextValue | null>(null);
